/**
 * useBackOr — le retour d'un écran poussé : la pile, sinon une route de repli (un lien ouvert à
 * froid n'a rien derrière lui). `fallbackParams` doit être stable (une constante du module).
 *
 * Sur le web, un lien ouvert à froid n'a pas une pile vide : React Navigation pose l'onglet (`menu`)
 * dessous. Quand le repli désigne un autre parent (Mon compte sous Paramètres), l'onglet seul dessous
 * vaut « rien derrière » : le repli passe avant la pile.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback } from "react"

const TAB_ROOT = "menu"

type BackNavigation = {
    canGoBack: () => boolean
    getState?: () => { index: number; routes: { name: string }[] } | undefined
    goBack: () => void
    replace: (route: string, params?: object) => void
}

export function useBackOr(fallback: string, fallbackParams?: object): () => void {
    const navigation = useNavigation() as unknown as BackNavigation
    return useCallback(() => {
        const state = navigation.getState?.()
        const below = state != null ? state.routes[state.index - 1]?.name : undefined
        const onlyTheTabBelow = below === TAB_ROOT && fallback !== TAB_ROOT
        if (navigation.canGoBack() && !onlyTheTabBelow) navigation.goBack()
        else if (fallbackParams != null) navigation.replace(fallback, fallbackParams)
        else navigation.replace(fallback)
    }, [navigation, fallback, fallbackParams])
}
